import { defineSketchObjectType, registerSketchObjectType } from '../registry';
import { SketchObjectAnchorKind, SketchObjectCategory, type SketchObjectFrame } from '../types';
import { at, pushSegment } from '../glyphs/frame';
import type { ParamValues } from '../paramSchema';
import type { Pt2 } from '../../utils/FloorMesh3DBuilder';
import { pushWalkArrow } from './stairCommon';
import { t } from '../../translation/markers';

const PARAMS = {
  stepCount: { kind: 'int', label: t('number of steps'), default: 12, min: 3, max: 60 },
  angle: { kind: 'float', label: t('rotation angle (°)'), default: 360, min: 90, max: 360, decimals: 0 },
  clockwise: { kind: 'boolean', label: t('clockwise'), default: false },
} as const;

/** Newel core radius as a fraction of the outer radius. */
const CORE_SCALE = 0.15;
/** Walk-line radius as a fraction of the outer radius (~the walking line). */
const WALK_SCALE = 0.65;
/** Segments approximating a full 360° of any of the ellipses. */
const FULL_TURN_SEGMENTS = 48;

/** Push an elliptical arc (semi-axes a/b in frame coords) from parametric
 *  angle t0 to t1 (0 = +u, π/2 = +n). */
function pushEllipseArc(out: number[], frame: SketchObjectFrame, a: number, b: number, t0: number, t1: number, segments: number): void {
  let prev: Pt2 | null = null;
  for (let i = 0; i <= segments; i++) {
    const t = t0 + ((t1 - t0) * i) / segments;
    const pt = at(frame, a * Math.cos(t), b * Math.sin(t));
    if (prev) pushSegment(out, prev, pt, frame.z);
    prev = pt;
  }
}

/**
 * Spiral (circular) stair: a point-stamped footprint like the round column —
 * the ellipse inscribed in width × depth (a circle for the default square
 * footprint). The glyph is the classic plan symbol: the swept outer arc, the
 * newel core in the center, one radial tread edge per step fanned over the
 * `rotation angle`, and the walk arrow curving up the stairs at ~2/3 radius
 * (cross tick at the entry, chevron at the exit). `clockwise` flips the turn
 * sense; the entry direction is set by rotating the object.
 */
function buildSpiralStairGlyph(frame: SketchObjectFrame, params: ParamValues<typeof PARAMS>): number[] {
  const out: number[] = [];
  const a = frame.width / 2;
  const b = frame.depth / 2;
  const spanDegrees = Math.min(360, Math.max(90, params.angle));
  const span = (spanDegrees * Math.PI) / 180;
  const sweepSign = params.clockwise ? -1 : 1;
  const fullTurn = spanDegrees >= 360 - 1e-3;
  const steps = Math.max(3, Math.round(params.stepCount));

  // Outer boundary over the swept span; the newel core is always a full ring.
  const outerSegments = Math.max(12, Math.ceil((FULL_TURN_SEGMENTS * spanDegrees) / 360));
  pushEllipseArc(out, frame, a, b, 0, sweepSign * span, outerSegments);
  pushEllipseArc(out, frame, a * CORE_SCALE, b * CORE_SCALE, 0, 2 * Math.PI, 24);

  // Radial tread edges — on a full turn the closing edge coincides with the
  // first one, so it is skipped.
  const lastEdge = fullTurn ? steps - 1 : steps;
  for (let k = 0; k <= lastEdge; k++) {
    const tk = (sweepSign * span * k) / steps;
    const cos = Math.cos(tk);
    const sin = Math.sin(tk);
    pushSegment(out, at(frame, a * CORE_SCALE * cos, b * CORE_SCALE * sin), at(frame, a * cos, b * sin), frame.z);
  }

  // Walk arrow along the walking-line arc, inset from both boundary radials.
  const walkInset = span * 0.08;
  const walkPoints: Array<readonly [number, number]> = [];
  for (let i = 0; i <= outerSegments; i++) {
    const tk = sweepSign * (walkInset + ((span - 2 * walkInset) * i) / outerSegments);
    walkPoints.push([WALK_SCALE * a * Math.cos(tk), WALK_SCALE * b * Math.sin(tk)]);
  }
  const headSize = Math.min(0.15, 0.35 * WALK_SCALE * Math.min(a, b));
  pushWalkArrow(out, frame, walkPoints, false, headSize);

  return out;
}

registerSketchObjectType(
  defineSketchObjectType({
    id: 'stair_spiral',
    label: t('Spiral stair'),
    category: SketchObjectCategory.FIXTURES,
    anchor: SketchObjectAnchorKind.POINT,
    constraint: { kind: 'FREE' },
    defaultSize: { width: 1.5, depth: 1.5 },
    params: PARAMS,
    glyph: { kind: 'parametric', build: buildSpiralStairGlyph },
  }),
);
